/**
 * CollectionConfigTab — Configuración de cobranza automática + templates.
 * Accesible desde /receivables/collection-config
 */
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Settings, Mail, Save, Loader2, Eye, ChevronDown, ChevronRight, Info, X, Clock, UserX } from 'lucide-react'
import { inputCls, labelCls } from '@/lib/formStyles'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import apiClient from '@/lib/apiClient'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCustomers } from '@/modules/sales/hooks/useSales'
import { TextField } from '@/components/ds/TextField'
import { Modal } from '@/components/ds/Modal'

// ─── Hooks locales ──────────────────────────────────────────────────────────

function useCollectionConfig() {
  return useQuery({
    queryKey: ['collection-config'],
    queryFn: async () => { const r = await apiClient.get('/finance/collection-config/'); return r.data },
  })
}

function useUpdateCollectionConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: any) => { const r = await apiClient.patch('/finance/collection-config/', data); return r.data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collection-config'] }),
  })
}

function useCollectionTemplates() {
  return useQuery({
    queryKey: ['collection-templates'],
    queryFn: async () => { const r = await apiClient.get('/finance/collection-templates/'); return r.data },
  })
}

function useUpdateTemplate(type: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: any) => { const r = await apiClient.patch(`/finance/collection-templates/${type}/`, data); return r.data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collection-templates'] }),
  })
}

function usePreviewTemplate() {
  return useMutation({
    mutationFn: async (data: { subject: string; body: string }) => {
      const r = await apiClient.post('/finance/collection-templates/preview/', data)
      return r.data
    },
  })
}

function useAddExclusion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (customerId: string) => {
      const r = await apiClient.post('/finance/collection-config/exclusions/', { customer_id: customerId })
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collection-config'] }),
  })
}

function useRemoveExclusion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (customerId: string) => {
      await apiClient.delete(`/finance/collection-config/exclusions/${customerId}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collection-config'] }),
  })
}

// ─── Variables disponibles ──────────────────────────────────────────────────

const VARIABLES_BASE = [
  { var: '{{cliente}}',       desc: 'Nombre del cliente' },
  { var: '{{rut_cliente}}',   desc: 'RUT del cliente' },
  { var: '{{empresa}}',       desc: 'Nombre de la empresa' },
  { var: '{{rut_empresa}}',   desc: 'RUT de la empresa' },
  { var: '{{monto_total}}',   desc: 'Total del/los documento(s)' },
  { var: '{{dias_mora}}',     desc: 'Días de mora (amistoso/firme/definitivo)' },
  { var: '{{cantidad_docs}}', desc: 'Cantidad de documentos' },
  { var: '{{fecha_hoy}}',     desc: 'Fecha actual' },
  { var: '{{tabla_deuda}}',   desc: 'Tabla HTML de documentos' },
]

const VARIABLES_PRE_DUE = [
  { var: '{{dias_para_vencer}}',  desc: 'Días hasta el vencimiento (pre-vencimiento)' },
  { var: '{{fecha_vencimiento}}', desc: 'Fecha de vencimiento (pre-vencimiento)' },
]

const TYPE_LABELS: Record<string, { label: string; desc: string; cls: string }> = {
  friendly: { label: 'Recordatorio Amistoso',  desc: 'Primer aviso — tono cordial',          cls: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30' },
  firm:     { label: 'Recordatorio Firme',     desc: 'Segundo aviso — tono formal',           cls: 'border-warning-border bg-warning-surface' },
  final:    { label: 'Aviso Definitivo',       desc: 'Último aviso — tono urgente',           cls: 'border-danger-border bg-danger-surface' },
  pre_due:  { label: 'Aviso Pre-vencimiento',  desc: 'Se envía N días antes del vencimiento', cls: 'border-info-border bg-info-surface' },
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function CollectionConfigTab() {
  const toast = useToast()
  const { data: config } = useCollectionConfig()
  const { mutate: updateConfig, isPending: savingConfig } = useUpdateCollectionConfig()
  const { data: templatesData } = useCollectionTemplates()
  const templates = templatesData?.templates || []

  const { mutate: addExclusion, isPending: addingExclusion } = useAddExclusion()
  const { mutate: removeExclusion } = useRemoveExclusion()
  const [selectedCustomerId, setSelectedCustomerId] = useState('')

  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const allCustomers = customersData?.customers || []
  const excludedIds = new Set((config?.pre_due_excluded_customers || []).map((c: any) => c.id))
  const availableCustomers = allCustomers.filter((c: any) => !excludedIds.has(c.id))

  const { register, handleSubmit, reset, getValues, formState: { errors } } = useForm()
  // Validación propia en vez de la del navegador (que mostraba los mensajes en
  // inglés): mismos límites que el backend.
  const reglaDias = (max?: number) => ({
    valueAsNumber: true,
    required: 'Obligatorio.',
    min: { value: 1, message: 'Debe ser al menos 1 día.' },
    ...(max ? { max: { value: max, message: `No puede superar ${max} días.` } } : {}),
  })
  const errorDe = (campo: string) => errors[campo]?.message as string | undefined

  useEffect(() => {
    if (config) reset(config)
  }, [config, reset])

  const handleSaveConfig = (data: any) => {
    // Excluir el campo de sólo lectura
    const { pre_due_excluded_customers, ...payload } = data
    updateConfig(payload, {
      onSuccess: () => toast.success('Configuración guardada'),
      onError: (e: any) => {
        const mensaje = extractErrorMessage(e, 'No se pudo guardar la configuración.')  // marca el error como mostrado
        const detalle = Object.values(e?.response?.data?.error?.details ?? {}).flat().join(' ')
        toast.error(detalle || mensaje)
      },
    })
  }

  const handleAddExclusion = () => {
    if (!selectedCustomerId) return
    addExclusion(selectedCustomerId, {
      onSuccess: () => { setSelectedCustomerId(''); toast.success('Cliente excluido') },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  const handleRemoveExclusion = (customerId: string) => {
    removeExclusion(customerId, {
      onSuccess: () => toast.success('Cliente eliminado de exclusiones'),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  return (
    <div className="space-y-8">

      {/* ═══ Configuración general (post-vencimiento) ═══ */}
      <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 shadow-card">
        <h3 className="text-sm font-bold text-content mb-4 flex items-center gap-2">
          <Settings size={16} className="text-primary" />
          Cobranza Automática (Post-Vencimiento)
        </h3>

        <form noValidate onSubmit={handleSubmit(handleSaveConfig)} className="space-y-3">
          <div className="flex items-center gap-4 p-3 bg-glass-3 rounded-lg">
            <label className="flex items-center gap-2 text-sm font-medium text-content cursor-pointer">
              <input type="checkbox" {...register('is_active')} className="rounded border-divider-strong" />
              Cobranza automática activa
            </label>
            <p className="text-xs text-content-subtle">Si está activo, se envían recordatorios automáticos diariamente a las 09:00 UTC.</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <TextField
              dense
              label="Días para Amistoso"
              inputProps={{ ...register('friendly_days', reglaDias()), type: 'number' }}
              error={errorDe('friendly_days')}
              hint="Primer recordatorio tras X días de mora"
            />
            <TextField
              dense
              label="Días para Firme"
              inputProps={{ ...register('firm_days', {
                ...reglaDias(),
                // Los niveles escalan: Amistoso < Firme < Definitivo (el backend lo exige igual).
                validate: v => (Number(getValues('friendly_days')) < v && v < Number(getValues('final_days')))
                  || 'Debe quedar entre los días de Amistoso y de Definitivo.',
              }), type: 'number' }}
              error={errorDe('firm_days')}
              hint="Segundo recordatorio"
            />
            <TextField
              dense
              label="Días para Definitivo"
              inputProps={{ ...register('final_days', reglaDias()), type: 'number' }}
              error={errorDe('final_days')}
              hint="Último aviso"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
              <input type="checkbox" {...register('attach_pdf')} className="rounded border-divider-strong" />
              Adjuntar PDF del DTE
            </label>
            <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
              <input type="checkbox" {...register('include_debt_table')} className="rounded border-divider-strong" />
              Incluir tabla de documentos en el email
            </label>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={savingConfig}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
              {savingConfig ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar configuración
            </button>
          </div>
        </form>
      </div>

      {/* ═══ Pre-vencimiento ═══ */}
      <div className="bg-glass backdrop-blur-glass border border-info-border rounded-card p-6 shadow-card">
        <h3 className="text-sm font-bold text-content mb-1 flex items-center gap-2">
          <Clock size={16} className="text-info-content-soft" />
          Recordatorio Pre-Vencimiento
        </h3>
        <p className="text-xs text-content-subtle mb-4">
          Envía un aviso automático a los clientes N días <strong>antes</strong> de que venza la factura.
          Se ejecuta diariamente a las 08:30 UTC.
        </p>

        <form noValidate onSubmit={handleSubmit(handleSaveConfig)} className="space-y-3">
          <div className="flex items-center gap-4 p-3 bg-info-surface rounded-lg border border-info-border">
            <label className="flex items-center gap-2 text-sm font-medium text-content cursor-pointer">
              <input type="checkbox" {...register('pre_due_enabled')} className="rounded border-divider-strong" />
              Aviso pre-vencimiento activo
            </label>
          </div>

          <div className="max-w-xs">
            <label htmlFor="collectionconfigtab-pre-due-days" className={labelCls}>Días antes del vencimiento</label>
            <input id="collectionconfigtab-pre-due-days" type="number" {...register('pre_due_days', reglaDias(60))} className={inputCls}
              aria-invalid={!!errorDe('pre_due_days')} aria-describedby={errorDe('pre_due_days') ? 'collectionconfigtab-pre-due-days-error' : undefined} />
            {errorDe('pre_due_days') && (
              <p id="collectionconfigtab-pre-due-days-error" className="text-xs text-danger-content mt-1">{errorDe('pre_due_days')}</p>
            )}
            <p className="text-xs text-content-subtle mt-1">
              Se envía cuando <code className="bg-surface-muted px-1 rounded text-xs">fecha_vencimiento = hoy + N días</code>
            </p>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={savingConfig}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-info-fill text-white rounded-lg hover:bg-info-fill-hover disabled:opacity-60">
              {savingConfig ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          </div>
        </form>

        {/* ── Clientes excluidos ── */}
        <div className="mt-6 border-t border-info-border pt-5">
          <h4 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-1 flex items-center gap-1.5">
            <UserX size={13} className="text-info-solid" />
            Clientes excluidos del pre-vencimiento
          </h4>
          <p className="text-xs text-content-subtle mb-4">
            Los clientes en esta lista no recibirán el aviso pre-vencimiento (el Cobrador los conoce y no requieren aviso anticipado).
          </p>

          {/* Agregar cliente */}
          <div className="flex gap-2 mb-4">
            <select aria-label="Seleccionar cliente para excluir"
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              className={`${inputCls} flex-1`}
            >
              <option value="">Seleccionar cliente para excluir...</option>
              {availableCustomers.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.legal_name}{c.tax_id ? ` — ${c.tax_id}` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selectedCustomerId || addingExclusion}
              onClick={handleAddExclusion}
              className="px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 whitespace-nowrap"
            >
              {addingExclusion ? <Loader2 size={14} className="animate-spin inline" /> : '+ Excluir'}
            </button>
          </div>

          {/* Lista de excluidos */}
          <div className="space-y-1.5">
            {(config?.pre_due_excluded_customers || []).map((c: any) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-info-surface border border-info-border rounded-lg">
                <div>
                  <span className="text-sm font-medium text-content">{c.legal_name}</span>
                  {c.tax_id && <span className="text-xs text-content-subtle ml-2">{c.tax_id}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExclusion(c.id)}
                  className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors"
                  title="Quitar exclusión"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {(config?.pre_due_excluded_customers || []).length === 0 && (
              <p className="text-xs text-content-subtle text-center py-4">
                Ningún cliente excluido — todos recibirán el aviso pre-vencimiento.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ═══ Variables disponibles ═══ */}
      <div className="bg-glass-3 border border-divider rounded-xl p-4">
        <h4 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Info size={12} /> Variables disponibles para los templates
        </h4>
        <div className="flex flex-wrap gap-2">
          {[...VARIABLES_BASE, ...VARIABLES_PRE_DUE].map(v => (
            <span key={v.var} className="inline-flex items-center gap-1 px-2 py-1 bg-surface border border-divider rounded-lg text-xs">
              <code className="text-primary font-mono font-semibold">{v.var}</code>
              <span className="text-content-subtle">— {v.desc}</span>
            </span>
          ))}
        </div>
      </div>

      {/* ═══ Templates editables ═══ */}
      <div>
        <h3 className="text-sm font-bold text-content mb-4 flex items-center gap-2">
          <Mail size={16} className="text-primary" />
          Templates de Email
        </h3>

        <div className="space-y-4">
          {templates.map((t: any) => (
            <TemplateEditor key={t.id} template={t} toast={toast} />
          ))}

          {templates.length === 0 && (
            <p className="text-sm text-content-subtle text-center py-8">
              Los templates se crean automáticamente al acceder por primera vez. Recarga la página.
            </p>
          )}
        </div>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

// ─── Template Editor ────────────────────────────────────────────────────────

function TemplateEditor({ template, toast }: { template: any; toast: any }) {
  const [expanded, setExpanded] = useState(false)
  const [subject, setSubject] = useState(template.subject)
  const [body, setBody] = useState(template.body)
  const [previewHtml, setPreviewHtml] = useState('')

  const { mutate: update, isPending: saving } = useUpdateTemplate(template.template_type)
  const { mutate: preview, isPending: previewing } = usePreviewTemplate()

  useEffect(() => {
    if (!previewHtml) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewHtml('') }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [previewHtml])

  const typeConf = TYPE_LABELS[template.template_type] || TYPE_LABELS.friendly

  const handleSave = () => {
    update({ subject, body }, {
      onSuccess: () => toast.success(`Template "${typeConf.label}" guardado`),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
    })
  }

  const handlePreview = () => {
    preview({ subject, body }, {
      onSuccess: (data: any) => setPreviewHtml(data.html),
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al generar preview')),
    })
  }

  return (
    <div className={`border border-divider rounded-xl overflow-hidden ${typeConf.cls}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={16} className="text-content-subtle" /> : <ChevronRight size={16} className="text-content-subtle" />}
          <div>
            <h4 className="text-sm font-bold text-content">{typeConf.label}</h4>
            <p className="text-xs text-content-muted">{typeConf.desc}</p>
          </div>
        </div>
        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${template.is_active ? 'bg-success-surface-strong text-success-content' : 'bg-surface-muted text-content-muted'}`}>
          {template.is_active ? 'Activo' : 'Inactivo'}
        </span>
      </div>

      {/* Editor */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-divider/50 pt-4 bg-surface">
          <TextField
            dense
            label="Asunto del email"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Recordatorio de pago — {{empresa}}"
          />

          <TextField
            dense
            label="Cuerpo del mensaje"
            multiline
            rows={8}
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Estimado/a {{cliente}}..."
            hint="Usa las variables listadas arriba. Los saltos de línea se convierten en párrafos."
          />

          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar template
            </button>
            <button onClick={handlePreview} disabled={previewing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-60">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Vista previa
            </button>
          </div>

          {/* Preview modal */}
          {previewHtml && (
            <Modal open onClose={() => setPreviewHtml('')} title={<>Vista previa — {typeConf.label}</>} size="2xl">
              <div className="flex-1 overflow-hidden">
                <iframe
                  srcDoc={previewHtml}
                  title="Preview"
                  className="w-full h-full border-0"
                  style={{ minHeight: '500px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </Modal>
          )}
        </div>
      )}
    </div>
  )
}
